<template>
  <div v-if="this.loggedInUser" class="dashboard-screen">
    <page-header :title="$t('app.dashboard.title')" />
    <div v-if="!isLoading" class="row">
      <div class="col-sm-7">
        <vue-cal
          :selected-date="this.today"
          :time-from="9 * 60"
          :disable-views="['years', 'year', 'week', 'day']"
          default-view="month"
          events-on-month-view="short"
          :locale="'hu'"
          :events="calendarTasks"
          style="height: 570px"
          :on-event-click="onEventClick"
        >
        </vue-cal>
      </div>
      <div class="col-sm-5">
        <div class="dashboard-cards">
          <dashboard-card
            :iconClass="'ti-calendar'"
            :count="taskCount"
            :percent="taskPercent"
            :activeLabel="$t('app.dashboard.workerDasboard.open')"
            :inactiveLabel="$t('app.dashboard.workerDasboard.done')"
            :title="'app.dashboard.workerDasboard.tasksCount'"
            :colSize="'col-sm-12'"
            class="dashboard-panel mb-20 pr-0"
          />
          <dashboard-card
            :iconClass="'ti-calendar'"
            :count="deflectionsCount"
            :percent="deflectionPercent"
            :activeLabel="$t('app.dashboard.workerDasboard.open')"
            :inactiveLabel="$t('app.dashboard.workerDasboard.done')"
            :title="'app.dashboard.workerDasboard.deflectionCount'"
            :colSize="'col-sm-12'"
            class="dashboard-panel mb-20 pr-0"
          />
          <dashboard-card
            :iconClass="'ti-eye'"
            :count="allInspectionCount"
            :percent="inspectionPercent"
            :activeLabel="$t('app.dashboard.vivuDashboard.done')"
            :inactiveLabel="$t('app.dashboard.vivuDashboard.open')"
            :title="'app.dashboard.vivuDashboard.inspectionCount'"
            :colSize="'col-sm-12'"
            class="dashboard-panel pr-0"
          />
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="page-wrapper loading-wrapper ml-0">
     <span class="loading-text">{{$t('app.listLoading')}}</span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ApexCharts from 'apexcharts';
import VueCal from 'vue-cal';
import 'vue-cal/dist/i18n/sr.js';
import 'vue-cal/dist/i18n/hu.js';
import 'vue-cal/dist/vuecal.css';
import PageHeader from '@/components/shared/layouts/PageHeader';
import BaseButton from '@/components/shared/buttons/BaseButton';
import ConfigurableList from '@/components/shared/lists/ConfigurableList';
import Paginator from '@/components/shared/navigations/Paginator';
import PercentCard from './PercentCard';
import DashboardCard from './DashboardCard';
import ActivityFeed from './ActivityFeed';
import ActivityChart from './ActivityChart';
import PieChart from './PieChart';
import ActivityTable from './ActivityTable';

export default {
  name: 'WorkerDashboardScreen',
  components: {
    'page-header': PageHeader,
    'base-button': BaseButton,
    'percent-card': PercentCard,
    'dashboard-card': DashboardCard,
    'activity-feed': ActivityFeed,
    'activity-chart': ActivityChart,
    'pie-chart': PieChart,
    'activity-table': ActivityTable,
    'configurable-list': ConfigurableList,
    'paginator-component': Paginator,
    ApexCharts,
    VueCal,
  },
  props: {},
  data() {
    return {
      tasks: undefined,
      calendarData: undefined,
      calendarTasks: undefined,
      deflectionsCount: 0,
      taskCount: 0,
      deflectionPercent: 0,
      taskPercent: 0,
      isLoading: true,
      inspections: undefined
    };
  },
  created() {
    this.fetchCalendarTasks({keyword: ''}).then((response) => {
      this.calendarData = response.final_tasks.task_list;
      this.deflectionsCount = response.final_tasks.deflection_count;
      this.taskCount = response.final_tasks.provision_count;
      this.deflectionPercent = response.final_tasks.deflection_percent;
      this.taskPercent = response.final_tasks.provision_percent;
      this.setTasks(this.calendarData);
      this.tasks = response.final_tasks.task_list;
    });
    this.fetchWorkerDashboard().then((response) => {
      this.inspections = response.inspections
      this.isLoading = false;
    })
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'loggedInUser',
    }),
    deflections() {
      return this.tasks.filter(r => r.task_type === 'deflection');
    },
    provisions() {
      return this.tasks.filter(r => r.task_type === 'provision');
    },
    today() {
      return new Date();
    },
    allInspectionCount() {
      return this.inspections.length;
    },
    doneInspections() {
      return this.inspections.filter(r => ['done', 'completed'].includes(r.status)).length;
    },
    inspectionPercent() {
      return (this.doneInspections / this.allInspectionCount) * 100;
    },
  },
  methods: {
    ...mapActions({
      fetchWorkerDashboard: 'dashboards/fetchWorkerDashboard',
      fetchCalendarTasks: 'tasks/fetchCalendarTasks',
    }),
    formatDate(d, withoutTime) {
      if (withoutTime) {
        return [
          d.getFullYear(),
          (`0${d.getMonth() + 1}`).slice(-2),
          d.getDate(),
        ].join('-');
      }
      return (
        `${[d.getFullYear(), d.getMonth() + 1, d.getDate()].join('-')
        } ${
          [d.getHours(), d.getMinutes()].join(':')}`
      );
    },
    onEventClick(event) {
      if (event.data.id === 0 && event.data.type === 0) {
        this.$swal
          .fire({
            html: '',
            type: 'info',
            showCloseButton: true,
            showCancelButton: true,
            showConfirmButton: false,
            focusConfirm: false,
            cancelButtonText: this.$t('app.calendar.goToTasks')
          })
          .then((result) => {
            if (result.value) {
              this.$router.push({
                name: 'InspectionsScreen',
              });
            } else if (result.dismiss && result.dismiss === 'cancel') {
              this.$router.push({
                name: 'TasksScreen',
              });
            }
          });
      } else if (event.data.id === 0 && event.data.type === 1) {
        this.$router.push({ name: 'InspectionsScreen' });
      } else if (event.data.id === 0 && event.data.type === 2) {
        this.$router.push({ name: 'TasksScreen' });
      } else if (event.data.type === 1) {
        this.$swal
          .fire({
            html: `<p class="name"><span class="title">${this.$t(
              'app.calendar.type',
            )}</span> ${this.$t('app.calendar.inspection')}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.identifier',
  )}</span> ${event.data.identifier}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.description',
  )}</span> ${event.data.description}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.deadline',
  )}</span> ${event.data.deadline}<p>`,
            type: 'info',
            showCloseButton: true,
            showCancelButton: false,
            showConfirmButton: false,
            focusConfirm: false,
          })
          .then((result) => {
            if (result.value) {
              this.$router.push({
                name: 'InspectionsScreen',
              });
            }
          });
      } else {
        this.$swal
          .fire({
            html: `<p class="name"><span class="title">${this.$t(
              'app.calendar.type',
            )}</span> ${event.data.task_type === 'deflection' ? this.$t('app.calendar.deflection') : this.$t('app.calendar.provision')}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.identifier',
  )}</span> ${event.data.identifier}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.description',
  )}</span> ${event.data.description}<p>
              <p class="name"><span class="title">${this.$t(
    'app.calendar.deadline',
  )}</span> ${event.data.deadline}<p>`,
            type: 'info',
            showCloseButton: true,
            showCancelButton: false,
            showConfirmButton: true,
            focusConfirm: false,
            confirmButtonText: this.$t('app.calendar.goToTasks'),
          })
          .then((result) => {
            if (result.value) {
              this.$router.push({
                name: 'TasksScreen',
                params: { id: event.data.id },
                query: { siteId: event.data.site_id },
              });
            }
          });
      }
    },
    setTasks(calendarData) {
      this.calendarTasks = calendarData.map((x) => {
        const date = new Date(x.deadline);
        if(!x.name && x.task_type && x.task_type === 'deflection') {
            return {
              title: this.$t('app.calendar.deflection'),
              data: {
                id: x.id,
                name: x.name === undefined || x.name === null ? this.$t('app.calendar.unset') : x.name,
                description: x.description === undefined || x.description === null ? this.$t('app.calendar.unset') : x.description,
                deadline: this.formatDate(date, true),
                identifier: x.identifier,
                type: x.type,
                site_id: x.site_id,
                task_type: x.task_type
              },
              start: this.formatDate(date),
              end: this.formatDate(date),
              class: x.status,
            }
          }
        if (!x.name && x.description) {
          return {
            title: x.description,
            data: {
              id: x.id,
              name:
                x.name === undefined || x.name === null
                  ? this.$t('app.calendar.unset')
                  : x.name,
              description:
                x.description === undefined || x.description === null
                  ? this.$t('app.calendar.unset')
                  : x.description,
              deadline: this.formatDate(date, true),
              identifier: x.identifier,
              type: x.type,
              site_id: x.site_id,
              task_type: x.task_type
            },
            start: this.formatDate(date),
            end: this.formatDate(date),
            class: x.status,
          };
        } else if (x.name) {
          return {
            title: x.name,
            data: {
              id: x.id,
              name:
                x.name === undefined || x.name === null
                  ? this.$t('app.calendar.unset')
                  : x.name,
              description:
                x.description === undefined || x.description === null
                  ? this.$t('app.calendar.unset')
                  : x.description,
              deadline: this.formatDate(date, true),
              identifier: x.identifier,
              type: x.type,
              site_id: x.site_id,
              task_type: x.task_type
            },
            start: this.formatDate(date),
            end: this.formatDate(date),
            class: x.status,
          };
        }
        return {
          title: x.identifier,
          data: {
            id: x.id,
            name:
                x.name === undefined || x.name === null
                  ? this.$t('app.calendar.unset')
                  : x.name,
            description:
                x.description === undefined || x.description === null
                  ? this.$t('app.calendar.unset')
                  : x.description,
            deadline: this.formatDate(date, true),
            identifier: x.identifier,
            type: x.type,
            site_id: x.site_id,
            task_type: x.task_type
          },
          start: this.formatDate(date),
          end: this.formatDate(date),
          class: x.status,
        };
      });
    },
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
/deep/.dashboard-panel {
  .card-view {
    margin-right: 0px !important;
  }
} 
.vuecal__flex {
  /deep/ .vuecal__body {
    .vuecal__cell {
      height: 74px !important;
    }
  }
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-text {
  font-size: 20px;
}
/deep/ .identifier-cell {
  width: 13%;
}
/deep/ .statement-cell {
  width: 35%;
}
/deep/ .location-cell {
  width: 11%;
}
/deep/ .extra-cell {
  width: 5%;
}
/deep/ .delete-cell {
  width: 5%;
}

.dasboard-row {
  margin-left: -4px;
  margin-right: -4px;
}

/deep/ .label {
  padding: 6px 30px 6px 30px;
  float: left;
  min-width: 130px;
  background: #d9534f;
}

/deep/ .label-built_in {
  background: #22af47;
}

/deep/ .label-not_built_in {
  background: #5bc0de;
}

.date-col {
  padding: 5px;
}
.small {
  height: 160px;
}
.large {
  height: 460px;
}

.title {
  font-size: 20px;
}

.statements {
  display: flex;
  align-items: center;
}

.without-float {
  margin-top: 10%;
  float: none !important;
}

.rounded {
  border-radius: 4px;
}

.with-padding {
  padding-bottom: 10px;
}

.card-wrapper {
  padding-top: 10px;
  margin-left: 0;
}

.industry-title {
  line-height: 5px !important;
}

.donut-panel {
  max-height: 390px;
}
</style>



// WEBPACK FOOTER //
// src/components/modules/dashboard/WorkerDasboard.vue